import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import { UserContext } from '../UserContext';
import axios from '../axiosInstance';
import '../Pages.css';
import '../Pips/Pips.css';
import '../Customers/Customers.css';

// Shown once per sign-in to users who review screening hits: existing KYC
// clients that the nightly list check found newly on the PIP / PEP, UN
// Sanctions or UK Sanctions lists. Approve = confirm the match, Reject =
// false positive — the same screening disposition as the client's screening
// panel (re-rates the client and is audited).

const LIST_LABEL = {
  pip: 'PIP',
  pep: 'PEP',
  un_sanctions_individual: 'UN Sanctions',
  un_sanctions_entity: 'UN Sanctions',
  uk_sanctions_individual: 'UK Sanctions',
  uk_sanctions_entity: 'UK Sanctions'
};

const SEEN_KEY = 'listMatchAlertsSeenFor';

const hasPermission = (user, name) =>
  !!user && (
    user.is_system_admin === true ||
    (Array.isArray(user.permissions) && user.permissions.includes(name))
  );

// Identifies the current sign-in, so the popup opens once per sign-in.
const signInMark = () => {
  try { return (localStorage.getItem('token') || '').slice(-32); } catch { return ''; }
};
const seenThisSignIn = () => {
  try { return sessionStorage.getItem(SEEN_KEY) === signInMark(); } catch { return false; }
};
const markSeen = () => {
  try { sessionStorage.setItem(SEEN_KEY, signInMark()); } catch { /* ignore */ }
};

const scorePill = (s) =>
  `un-meta__pill ${s >= 90 ? 'cust-pill--risk-high' : s >= 70 ? 'cust-pill--risk-medium' : 'cust-pill--risk-low'}`;

function ListMatchAlerts() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const canReview = hasPermission(user, 'screening.review');

  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState({});   // hit id -> true while saving
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user || !canReview || seenThisSignIn()) return undefined;
    let cancelled = false;
    axios.get('/screening/list-alerts')
      .then((r) => {
        if (cancelled) return;
        const rows = Array.isArray(r.data) ? r.data : [];
        setItems(rows);
        if (rows.length) setOpen(true);
        else markSeen();
      })
      .catch(() => { /* silent: the bell and the KYC page still show them */ });
    return () => { cancelled = true; };
  }, [user, canReview]);

  const close = useCallback(() => {
    markSeen();
    setOpen(false);
  }, []);

  // Every match decided: done for this sign-in.
  useEffect(() => {
    if (open && items.length === 0) close();
  }, [open, items.length, close]);

  const decide = async (hit, disposition) => {
    setBusy((b) => ({ ...b, [hit.id]: true }));
    setError(null);
    try {
      await axios.post(`/screening/hits/${hit.id}/disposition`, { disposition });
      setItems((rows) => rows.filter((x) => x.id !== hit.id));
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Could not save the decision');
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[hit.id]; return n; });
    }
  };

  const openClient = (hit) => {
    close();
    navigate(`/customers?id=${hit.customer_id}`);
  };

  if (!open || !items.length) return null;

  const clients = new Set(items.map((x) => x.customer_id)).size;

  return (
    <div className="modal-overlay" onClick={close}>
      <div className="modal-content" style={{ maxWidth: 900 }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="list-match-alerts-title">
        <div className="modal-header">
          <h2 id="list-match-alerts-title"><FaExclamationTriangle /> New list matches</h2>
          <button type="button" className="modal-close" onClick={close} aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          <div className="alert-banner alert-banner--danger" style={{ marginBottom: '0.75rem' }}>
            <span className="alert-banner__body">
              {clients} existing client{clients === 1 ? '' : 's'} {clients === 1 ? 'was' : 'were'} found on the PIP / PEP, UN Sanctions
              or UK Sanctions lists by the daily list check. They were not flagged before.
            </span>
          </div>
          <p style={{ fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
            <strong>Approve</strong> if the listed person or entity is your client (confirmed match).{' '}
            <strong>Reject</strong> if it isn't (false positive). The client's risk rating is updated with each decision.
          </p>
          {error && <div className="form-error" style={{ marginBottom: '0.5rem' }}>{error}</div>}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color,#e3e6ea)', textAlign: 'left' }}>
                  <th style={{ padding: '0.45rem 0.5rem' }}>Client</th>
                  <th style={{ padding: '0.45rem 0.5rem' }}>List</th>
                  <th style={{ padding: '0.45rem 0.5rem' }}>Listed as</th>
                  <th style={{ padding: '0.45rem 0.5rem' }}>Score</th>
                  <th style={{ padding: '0.45rem 0.5rem', textAlign: 'right' }}>Decision</th>
                </tr>
              </thead>
              <tbody>
                {items.map((h) => (
                  <tr key={h.id} style={{ borderBottom: '1px solid var(--border-color,#eef0f3)' }}>
                    <td style={{ padding: '0.5rem' }}>
                      <button
                        type="button"
                        onClick={() => openClient(h)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', textAlign: 'left', textDecoration: 'underline' }}
                        title="Open the client"
                      >
                        <strong>{h.customer_name}</strong>
                      </button>
                      {h.external_reference && (
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary,#607d8b)' }}>{h.external_reference}</div>
                      )}
                    </td>
                    <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>
                      {LIST_LABEL[h.source] || h.source.replace(/_/g, ' ')}
                      {h.source_reference && (
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary,#607d8b)' }}>{h.source_reference}</div>
                      )}
                    </td>
                    <td style={{ padding: '0.5rem' }}>
                      <div>{h.matched_name}</div>
                      {h.matched_alias && (
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary,#607d8b)' }}>via alias: {h.matched_alias}</div>
                      )}
                    </td>
                    <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>
                      <span className={scorePill(Number(h.match_score))}>{Number(h.match_score)} · {h.match_type}</span>
                    </td>
                    <td style={{ padding: '0.5rem', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      <button
                        type="button"
                        className="btn-cancel"
                        style={{ padding: '0.2rem 0.55rem', fontSize: '0.75rem', marginRight: '0.3rem' }}
                        disabled={!!busy[h.id]}
                        title="Not this client — mark as false positive"
                        onClick={() => decide(h, 'false_positive')}
                      >
                        <FaTimes /> Reject
                      </button>
                      <button
                        type="button"
                        className="export-button"
                        style={{ padding: '0.2rem 0.55rem', fontSize: '0.75rem' }}
                        disabled={!!busy[h.id]}
                        title="This is the client — confirm the match"
                        onClick={() => decide(h, 'confirmed')}
                      >
                        <FaCheck /> Approve
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.9rem' }}>
            <button type="button" className="btn-cancel" onClick={close}>Review later</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ListMatchAlerts;
